import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../services/apiService';
import QuickPhotoButton from '../QuickPhotoButton';
const DESIGNATIONS_PREDEFINIES = [
  "GENERAL ARMOIRE",
  "GENERAL ECLAIRAGE",
  "GENERAL PRISE",
  "GENERAL CLIM",
  "CIRCUIT PRISE",
  "CIRCUIT ECLAIRAGE",
  "CIRCUIT CLIMATISATION",
  "CIRCUIT CHAUFFAGE",
  "Alimentation tableau secondaire",
  "Chauffe-eau",
  "Lave-linge",
  "Lave-vaisselle",
  "Four",
  "Réfrigérateur",
  "Congélateur",
  "Sèche-linge",
  "VMC",
  "Volets roulants",
  "Prises cuisine",
  "Prises séjour",
  "Prises chambres",
  "Prises salle de bain",
  "Éclairage cuisine",
  "Éclairage séjour",
  "Éclairage chambres",
  "Éclairage salle de bain",
  "Chauffage électrique cuisine",
  "Chauffage électrique séjour",
  "Chauffage électrique chambres",
  "Chauffage électrique salle de bain",
  "Alarme",
  "Portail",
  "Interphone",
  "Extérieur",
  "Pompe piscine",
  "Prise extérieure",
  "Éclairage extérieur"
];
const TYPES_DISJONCTEUR = [
  "D21", "D22", "DD21", "DD22", "D44", "DD44", "D43", "DD43", "D33", "DD33",
  "I2", "CI", "I4", "ID2", "ID4",
  "FU21", "FU22", "FU33", "FU44", "FU43",
  "Fm21", "Fm22", "Fm33", "Fm44",
  "RT"
];
const INTENSITES_NOMINALES = ["2", "6", "10", "16", "20", "25", "32", "40", "63", "80", "100", "125", "250", "400"];
const COURBES = ["B", "C", "D", "Z", "MA", "UG"];
const PDC_VALUES = ["4.5", "6", "10", "15", "20", "25", "36", "50"];
const SECTIONS = [
  "3G1.5", "3G2.5", "3G6", "5G1.5", "5G2.5", "5G6", "5G4", "5G10", "3G4", "4G2.5", "3X10", "2X16",
  "5G16", "5G25", "5G35", "4X50", "4X35", "4X70", "4X95", "4X120", "CI", "JDB"
];
const IZ_COEF_VALUES = ["0.72", "0.74", "0.76", "0.8", "0.84", "0.9", "1"];
const IDN_VALUES = ["10", "30", "100", "300", "500", "650", "1000"];
const TEMPO_VALUES = ["0", "60", "150", "200", "S", "Instantané", "Court", "Sélectif"];
const ESSAI_OPTIONS = ["B", "M", "NV", "SO"];

const GRILLE_JSON_SCHEMA = {
  type: "object",
  properties: {
    meta: {
      type: "object",
      properties: {
        ik3: { type: "string" },
        armoire: { type: "string" },
        localisation: { type: "string" },
        date: { type: "string" },
        armoire_amont: { type: "string" },
      },
      additionalProperties: true,
    },
    circuits: {
      type: "array",
      items: {
        type: "object",
        properties: {
          numero: { type: "string" },
          designation: { type: "string" },
          type: { type: "string" },
          in: { type: "string" },
          courbe: { type: "string" },
          pdc: { type: "string" },
          section: { type: "string" },
          iz_coef: { type: "string" },
          idn: { type: "string" },
          tempo: { type: "string" },
          essai: { type: "string" },
          isol: { type: "string" },
        },
        additionalProperties: false,
      },
    },
  },
  required: ["circuits"],
};

const buildGrillePrompt = () => `
Tu es un expert en vérification électrique. Analyse la photo ou le PDF d'une grille manuscrite de circuits d'armoire.

Objectif:
- Extraire tous les circuits et les valeurs par colonne.
- Retourner un JSON strictement conforme au schéma fourni.

Important (mise en page):
- La feuille peut être tournée. Oriente mentalement l'image pour lire l'en-tête "GRILLE DE RELEVÉ CIRCUITS ÉLECTRIQUES".
- Lis par colonnes alignées (de gauche à droite) et par lignes (de haut en bas).
- Ignore la ligne d'en-tête et toute zone hors tableau (marges, titres, logos).

Colonnes attendues (12 colonnes):
1. numero : RANG hiérarchique uniquement (1 = circuit principal, 2 = sous-circuit, 3 = niveau 3, 4 = niveau 4). Valeur TOUJOURS entre 1 et 4. Si la numérotation est hiérarchique (1, 1.1, 1.2, 2…), déduire le rang. Sinon, mettre "1" par défaut. Ne jamais mettre le numéro de ligne séquentiel.
2. designation (désignation du circuit)
3. type (type de protection)
4. in (intensité nominale en A)
5. courbe
6. pdc (kA)
7. section
8. iz_coef
9. idn (mA)
10. tempo
11. essai
12. isol (MOhm)

Règles:
- Remplis chaque champ par une chaîne (string). Si vide/illisible -> "".
- Ne pas inventer des valeurs.
- Ne reporte PAS une valeur d'une ligne à l'autre si la case est vide.
- Si un auteur utilise un signe de répétition (ex: guillemet/ditto), recopie la valeur de la ligne au-dessus uniquement pour cette colonne.
- Gérer les ambiguïtés (1 vs l, 0 vs O, D vs O) sans corriger arbitrairement.
- Conserver les unités uniquement si elles sont écrites (sinon valeur brute).
- Ne déduis jamais tempo/essai/idn/isol s'ils ne sont pas explicitement écrits dans la cellule.
- Si une colonne est entièrement vide sur l'image, laisse toutes les valeurs de cette colonne à "".
- Retourner aussi meta si visible (ik3, armoire, localisation, date, armoire_amont).
`;

const toText = (value) => (value === null || value === undefined ? '' : String(value).trim());
const clampRang = (raw = {}) => {
  const v = toText(raw.numero ?? raw.no ?? raw.n ?? raw['n°'] ?? raw['N°']);
  const n = parseInt(v, 10);
  if (!isNaN(n) && n >= 1 && n <= 4) return String(n);
  return '1';
};

const normalizeCircuit = (raw = {}) => ({
  numero: clampRang(raw),
  designation: toText(raw.designation ?? raw.designations ?? raw.libelle),
  type: toText(raw.type ?? raw.type_protection),
  in: toText(raw.in ?? raw.in_a ?? raw.intensite ?? raw.intensite_nominale),
  courbe: toText(raw.courbe),
  pdc: toText(raw.pdc ?? raw.pdc_ka),
  section: toText(raw.section),
  iz_coef: toText(raw.iz_coef ?? raw.izcoefficient ?? raw.iz),
  idn: toText(raw.idn ?? raw.idn_ma),
  tempo: toText(raw.tempo ?? raw.temporisation),
  essai: toText(raw.essai),
  isol: toText(raw.isol ?? raw.isolation),
  obs_ids: [],
});
const filterCircuit = (circuit) =>
  Object.values(circuit).some((value) => value && value !== '-' && value !== 'null');

const emptyCircuit = {
  numero: '1', // Rang 1 par défaut (cohérent avec l'affichage du select)
  designation: '',
  type: '',
  in: '',
  courbe: '',
  pdc: '',
  section: '',
  iz_coef: '',
  idn: '',
  tempo: '',
  essai: '',
  isol: '',
  obs_ids: []
};

export default function CircuitsModal({ 
  armoire, 
  armoireIndex,
  missionId,
  circuits: initialCircuits, 
  onUpdateCircuits, 
  onClose,
  onObservationCircuit,
  onQuickPhotoOpenObs,
}) {
  const [circuits, setCircuits] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentCircuit, setCurrentCircuit] = useState(emptyCircuit);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateIndex, setDuplicateIndex] = useState(null);
  const [duplicateChoice, setDuplicateChoice] = useState('1');
  const [grilleImporting, setGrilleImporting] = useState(false);
  const [grilleStep, setGrilleStep] = useState('');
  const [grilleError, setGrilleError] = useState('');
  const [grilleSuccess, setGrilleSuccess] = useState('');
  const [grilleDetection, setGrilleDetection] = useState(null);
  const modalContentRef = useRef(null);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const initial = initialCircuits || [];
    setCircuits(initial);
    // À l'ouverture, on bascule directement en mode « saisie d'une nouvelle ligne »
    // pour éviter le placeholder vide et le clic supplémentaire.
    setEditingIndex(initial.length);
    setCurrentCircuit(emptyCircuit);
  }, [initialCircuits]);

  useEffect(() => {
    if (!grilleSuccess) return;
    const t = setTimeout(() => setGrilleSuccess(''), 5000);
    return () => clearTimeout(t);
  }, [grilleSuccess]);

  useEffect(() => {
    if (!grilleError) return;
    const t = setTimeout(() => setGrilleError(''), 10000);
    return () => clearTimeout(t);
  }, [grilleError]);

  // ✅ Protection : Si armoire n'est pas définie, ne pas afficher le modal
  if (!armoire) {
    return null;
  }

  const startEditCircuit = (index) => {
    setEditingIndex(index);
    setCurrentCircuit({ ...circuits[index] });
    setTimeout(() => {
      modalContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const handleDeleteCircuit = (index) => {
    if (window.confirm('Supprimer ce circuit ?')) {
      const newCircuits = circuits.filter((_, i) => i !== index);
      setCircuits(newCircuits);
      onUpdateCircuits(newCircuits);
      // Toujours revenir au mode « saisie d'une nouvelle ligne » après suppression
      setEditingIndex(newCircuits.length);
      setCurrentCircuit(emptyCircuit);
    }
  };

  // 🔥 Fonctions de duplication
  const duplicateBelow = (index) => {
    const clonedCircuit = { ...circuits[index], obs_ids: [] };
    const newCircuits = [
      ...circuits.slice(0, index + 1),
      clonedCircuit,
      ...circuits.slice(index + 1)
    ];
    setCircuits(newCircuits);
    onUpdateCircuits(newCircuits);
  };

  const duplicateAtEnd = (index) => {
    const clonedCircuit = { ...circuits[index], obs_ids: [] };
    const newCircuits = [...circuits, clonedCircuit];
    setCircuits(newCircuits);
    onUpdateCircuits(newCircuits);
  };

  const duplicateWithChildren = (index) => {
    const sourceRank = parseInt(circuits[index].numero);
    const toDuplicate = [circuits[index]];
    
    for (let i = index + 1; i < circuits.length; i++) {
      const currentRank = parseInt(circuits[i].numero);
      if (currentRank <= sourceRank) break;
      toDuplicate.push(circuits[i]);
    }
    
    const clonedGroup = toDuplicate.map(c => ({ ...c, obs_ids: [] }));
    const insertAt = index + toDuplicate.length;
    const newCircuits = [
      ...circuits.slice(0, insertAt),
      ...clonedGroup,
      ...circuits.slice(insertAt)
    ];
    setCircuits(newCircuits);
    onUpdateCircuits(newCircuits);
  };

  const handleObservationCircuit = (circuit, index) => {
    if (onObservationCircuit) {
      onObservationCircuit(circuit, index);
    }
  };

  const buildCircuitContext = (circuit, index) => ({
    step_origin: 'etape_9_circuits',
    armoire_index: armoireIndex,
    armoire_designation: armoire?.designation || '',
    circuit_index: index,
    circuit_designation: circuit?.designation || '',
    localisation: armoire?.localisation || 'NON DÉFINI',
    element: `Circuit ${circuit?.designation || ''} (${armoire?.designation || 'Armoire'})`,
    circuit: circuit?.numero || '',
    element_ref: {
      etape: '9',
      type: 'circuit',
      path: {
        armoire_index: armoireIndex,
        circuit_index: index,
      },
    },
  });

  const handleQuickPhotoCircuit = (photo, context) => {
    if (typeof onQuickPhotoOpenObs === 'function') {
      onQuickPhotoOpenObs(photo, context);
    }
  };

  // Le formulaire en cours est exploitable dès qu'une désignation est saisie
  // (le rang est toujours présent car initialisé à '1' par défaut).
  const isCurrentValid = () => !!(currentCircuit.designation && currentCircuit.designation.trim());

  // Le formulaire en cours est « vide » si l'utilisateur n'a rien saisi
  // (on ignore numero car il a une valeur par défaut '1').
  const isCurrentEmpty = () =>
    !currentCircuit.designation && !currentCircuit.type
    && !currentCircuit.in && !currentCircuit.courbe && !currentCircuit.pdc
    && !currentCircuit.section && !currentCircuit.iz_coef && !currentCircuit.idn
    && !currentCircuit.tempo && !currentCircuit.essai && !currentCircuit.isol;

  // Sauvegarde le circuit courant. Retourne le tableau résultant ou null si invalide.
  const saveCurrentCircuit = () => {
    if (editingIndex === null) return circuits;
    const nextCircuits = [...circuits];
    if (editingIndex === nextCircuits.length) {
      nextCircuits.push({ ...currentCircuit, obs_ids: currentCircuit.obs_ids || [] });
    } else {
      nextCircuits[editingIndex] = { ...currentCircuit, obs_ids: currentCircuit.obs_ids || [] };
    }
    setCircuits(nextCircuits);
    onUpdateCircuits(nextCircuits);
    return nextCircuits;
  };

  // Bouton « Enregistrer + Nouveau » : sauve la saisie + ouvre une nouvelle ligne vide
  const handleSaveAndAddNext = () => {
    if (!isCurrentValid()) {
      alert('Veuillez renseigner au moins la désignation du circuit.');
      return;
    }
    const result = saveCurrentCircuit();
    if (!result) return;
    setEditingIndex(result.length);
    setCurrentCircuit(emptyCircuit);
    setTimeout(() => {
      modalContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  // Bouton « Fermer » : sauve la saisie en cours si valide, puis ferme.
  // Si formulaire vide → ferme directement.
  // Si formulaire partiellement rempli mais invalide → demande confirmation.
  const handleSaveAndClose = () => {
    if (editingIndex === null || isCurrentEmpty()) {
      onClose();
      return;
    }
    if (!isCurrentValid()) {
      const ok = window.confirm(
        "Le circuit en cours de saisie est incomplet (désignation manquante).\n\nFermer sans enregistrer cette ligne ?"
      );
      if (!ok) return;
      onClose();
      return;
    }
    saveCurrentCircuit();
    onClose();
  };

  const openDuplicateModal = (index) => {
    setDuplicateIndex(index);
    setDuplicateChoice('1');
    setShowDuplicateModal(true);
  };

  const confirmDuplicate = () => {
    if (duplicateIndex === null) return;
    if (duplicateChoice === '1') duplicateBelow(duplicateIndex);
    else if (duplicateChoice === '2') duplicateAtEnd(duplicateIndex);
    else if (duplicateChoice === '3') duplicateWithChildren(duplicateIndex);
    setShowDuplicateModal(false);
    setDuplicateIndex(null);
  };

  // Compresse une image via Canvas pour rester sous 4 MB (limite Claude API = 5 MB)
  const compressImageFile = (file, maxSizeMB = 4, maxDimension = 1920) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > maxDimension || height > maxDimension) {
            if (width >= height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);

          const tryQuality = (quality) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) { resolve(file); return; }
                if (blob.size <= maxSizeMB * 1024 * 1024 || quality <= 0.4) {
                  resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                  }));
                } else {
                  tryQuality(Math.round((quality - 0.1) * 10) / 10);
                }
              },
              'image/jpeg',
              quality
            );
          };
          tryQuality(0.85);
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const openCameraCapture = () => {
    if (grilleImporting) return;
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
      cameraInputRef.current.click();
    }
  };

  const openFileChooser = () => {
    if (grilleImporting) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleCameraInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleGrilleImport(file);
  };

  const handleFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleGrilleImport(file);
  };

  const handleGrilleImport = async (file) => {
    if (!file) return;
    console.log('[CircuitsModal] handleGrilleImport start', { name: file.name, type: file.type, size: file.size });
    setGrilleImporting(true);
    setGrilleStep('Préparation…');
    setGrilleError('');
    setGrilleSuccess('');
    setGrilleDetection(null);
    try {
      // Étape 1 : compression si image trop grande
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        const originalMB = (file.size / 1024 / 1024).toFixed(1);
        setGrilleStep(`Compression de l'image (${originalMB} MB)…`);
        try {
          fileToUpload = await compressImageFile(file);
        } catch (compressErr) {
          console.warn('[CircuitsModal] compressImageFile failed, fallback original', compressErr);
          fileToUpload = file;
        }
        const compressedMB = (fileToUpload.size / 1024 / 1024).toFixed(1);
        setGrilleStep(`Envoi de l'image (${compressedMB} MB)…`);
      } else {
        setGrilleStep("Envoi du fichier…");
      }

      // Étape 2 : upload
      console.log('[CircuitsModal] upload start', { size: fileToUpload.size });
      const upload = await apiService.uploadFile(fileToUpload, 'grille');
      console.log('[CircuitsModal] upload result', upload);
      if (!upload?.success || !upload?.file_url) {
        throw new Error(upload?.error || "Erreur lors de l'upload");
      }

      // Étape 3 : analyse IA
      setGrilleStep("Détection du format de la grille…");
      console.log('[CircuitsModal] analyzeArmoireImage start', upload.file_url);
      const analysis = await apiService.analyzeArmoireImage(upload.file_url);
      console.log('[CircuitsModal] analyzeArmoireImage result', analysis);
      if (!analysis?.success) {
        throw new Error(analysis?.error || "Erreur d'analyse de la grille");
      }
      if (analysis.detection) {
        setGrilleDetection(analysis.detection);
      }

      setGrilleStep("Extraction des circuits…");
      const extractedCircuits = Array.isArray(analysis.data?.circuits) ? analysis.data.circuits : [];
      const normalizedCircuits = extractedCircuits.map(normalizeCircuit).filter(filterCircuit);

      if (normalizedCircuits.length === 0) {
        setGrilleError("Aucun circuit n'a pu être extrait. Essayez une photo plus nette ou un autre format.");
        return;
      }

      const newCircuits = [...circuits, ...normalizedCircuits];
      setCircuits(newCircuits);
      onUpdateCircuits(newCircuits);
      setGrilleSuccess(`${normalizedCircuits.length} circuits importés.`);
    } catch (error) {
      console.error('[CircuitsModal] handleGrilleImport error', error);
      setGrilleError(error?.message || "Erreur lors de l'analyse de la grille.");
    } finally {
      setGrilleImporting(false);
      setGrilleStep('');
    }
  };

  return (
    <>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    <input
      ref={cameraInputRef}
      type="file"
      accept="image/*"
      capture="environment"
      onChange={handleCameraInputChange}
      style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      aria-hidden="true"
    />
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*,application/pdf"
      onChange={handleFileInputChange}
      style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      aria-hidden="true"
    />

    {grilleImporting && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.75)',
          zIndex: 100000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 16,
            padding: 28,
            maxWidth: 360,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              margin: '0 auto 16px',
              border: '5px solid #e2e8f0',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
            {grilleStep || 'Analyse en cours…'}
          </div>
          <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
            Compression → Envoi → Détection du format → Extraction des circuits
            <br />
            <span style={{ fontSize: 11, color: '#94a3b8' }}>(15 à 30 secondes)</span>
          </div>
        </div>
      </div>
    )}

    {!grilleImporting && (grilleSuccess || grilleError) && (
      <div
        style={{
          position: 'fixed',
          left: '50%',
          bottom: 24,
          transform: 'translateX(-50%)',
          zIndex: 100001,
          padding: '14px 20px',
          borderRadius: 12,
          fontSize: 14,
          fontWeight: 600,
          color: 'white',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          background: grilleSuccess
            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          maxWidth: '90vw',
          textAlign: 'center',
          cursor: 'pointer',
        }}
        onClick={() => {
          setGrilleSuccess('');
          setGrilleError('');
        }}
      >
        {grilleSuccess ? `✅ ${grilleSuccess}` : `❌ ${grilleError}`}
      </div>
    )}

    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* En-tête */}
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>⚙️ Circuits</h3>
            <p style={styles.subtitle}>{armoire?.designation || 'Armoire'}</p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        {/* Contenu */}
        <div style={styles.content} ref={modalContentRef}>
          {/* Formulaire inline */}
          <div style={styles.editorCard}>
            <div style={styles.editorHeader}>
              <div style={styles.editorTitle}>
                {editingIndex === null
                  ? 'Saisie des circuits'
                  : editingIndex === circuits.length
                    ? '➕ Nouveau circuit'
                    : '✏️ Modifier circuit'}
              </div>
            </div>

            {editingIndex === null ? (
              <div style={styles.editorPlaceholder}>
                Cliquez sur ✏️ d'une ligne pour la modifier, ou sur « 💾 Enregistrer + Nouveau » pour ajouter un circuit.
              </div>
            ) : (
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Rang (1–4)</label>
                  <select
                    style={styles.formSelect}
                    value={currentCircuit.numero || '1'}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, numero: e.target.value }))}
                  >
                    <option value="1">1 — Principal</option>
                    <option value="2">2 — Sous-circuit</option>
                    <option value="3">3 — Niveau 3</option>
                    <option value="4">4 — Niveau 4</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Désignation</label>
                  <input
                    style={styles.formInput}
                    list="designations"
                    value={currentCircuit.designation}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, designation: e.target.value }))}
                    placeholder="Ex: CIRCUIT PRISE"
                  />
                  <datalist id="designations">
                    {DESIGNATIONS_PREDEFINIES.map((item) => (
                      <option key={item} value={item} />
                    ))}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Type</label>
                  <input
                    style={styles.formInput}
                    list="cm-types"
                    value={currentCircuit.type}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, type: e.target.value }))}
                    placeholder="Ex: D21, DD22…"
                  />
                  <datalist id="cm-types">
                    {TYPES_DISJONCTEUR.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>In (A)</label>
                  <input
                    style={styles.formInput}
                    list="cm-intensites"
                    value={currentCircuit.in}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, in: e.target.value }))}
                    placeholder="Ex: 16, 20…"
                  />
                  <datalist id="cm-intensites">
                    {INTENSITES_NOMINALES.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Courbe</label>
                  <input
                    style={styles.formInput}
                    list="cm-courbes"
                    value={currentCircuit.courbe}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, courbe: e.target.value }))}
                    placeholder="Ex: C, B…"
                  />
                  <datalist id="cm-courbes">
                    {COURBES.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>PDC (kA)</label>
                  <input
                    style={styles.formInput}
                    list="cm-pdc"
                    value={currentCircuit.pdc}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, pdc: e.target.value }))}
                    placeholder="Ex: 4.5, 6…"
                  />
                  <datalist id="cm-pdc">
                    {PDC_VALUES.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Section</label>
                  <input
                    style={styles.formInput}
                    list="cm-sections"
                    value={currentCircuit.section}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, section: e.target.value }))}
                    placeholder="Ex: 3G2.5…"
                  />
                  <datalist id="cm-sections">
                    {SECTIONS.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Iz Coef</label>
                  <input
                    style={styles.formInput}
                    list="cm-iz"
                    value={currentCircuit.iz_coef}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, iz_coef: e.target.value }))}
                    placeholder="Ex: 0.8, 1…"
                  />
                  <datalist id="cm-iz">
                    {IZ_COEF_VALUES.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Idn (mA)</label>
                  <input
                    style={styles.formInput}
                    list="cm-idn"
                    value={currentCircuit.idn}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, idn: e.target.value }))}
                    placeholder="Ex: 30, 300…"
                  />
                  <datalist id="cm-idn">
                    {IDN_VALUES.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Tempo</label>
                  <input
                    style={styles.formInput}
                    list="cm-tempo"
                    value={currentCircuit.tempo}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, tempo: e.target.value }))}
                    placeholder="Ex: S, 60…"
                  />
                  <datalist id="cm-tempo">
                    {TEMPO_VALUES.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Essai</label>
                  <input
                    style={styles.formInput}
                    list="cm-essai"
                    value={currentCircuit.essai}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, essai: e.target.value }))}
                    placeholder="Ex: B, M, NV…"
                  />
                  <datalist id="cm-essai">
                    {ESSAI_OPTIONS.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Isol (MΩ)</label>
                  <input
                    style={styles.formInput}
                    value={currentCircuit.isol}
                    onChange={(e) => setCurrentCircuit((prev) => ({ ...prev, isol: e.target.value }))}
                    placeholder="Ex: 1"
                  />
                </div>
              </div>
            )}
          </div>

          {grilleImporting && (
            <div style={styles.importLoading}>
              <div style={styles.importLoadingSpinner}></div>
              <div>
                <div style={styles.importLoadingTitle}>
                  {grilleStep || 'Traitement en cours…'}
                </div>
                <div style={styles.importLoadingSubtitle}>
                  Compression → Envoi → Détection du format → Extraction des circuits (15–30 sec)
                </div>
              </div>
            </div>
          )}

          {!grilleImporting && (grilleDetection || grilleError || grilleSuccess) && (
            <div style={styles.importMessage}>
              {grilleDetection && (
                <div style={styles.detectedBadge}>
                  <span style={styles.detectedLabel}>
                    Format détecté : {grilleDetection.format || 'Inconnu'}
                  </span>
                  {grilleDetection.confidence && (
                    <span style={styles.detectedConfidence}>
                      Confiance : {grilleDetection.confidence}
                    </span>
                  )}
                </div>
              )}
              {grilleError && <div style={styles.importError}>{grilleError}</div>}
              {grilleSuccess && <div style={styles.importSuccess}>{grilleSuccess}</div>}
            </div>
          )}

          {/* Liste des circuits — tableau compact */}
          {circuits.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🔌</div>
              <p style={styles.emptyText}>Aucun circuit</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f1f5f9' }}>
                    {['#', 'Rg', 'Désignation', 'Type', 'In', 'Courbe', 'Pdc', 'Section', 'Iz/C', 'Idn', 'Tempo', 'Essai', 'Isol', 'Obs', 'Actions'].map((h) => (
                      <th key={h} style={tableStyles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {circuits.map((circuit, index) => {
                    const isEditing = editingIndex === index;
                    const rang = circuit.numero || '1';
                    return (
                      <tr
                        key={index}
                        style={{
                          backgroundColor: isEditing ? '#eff6ff' : (index % 2 === 0 ? '#ffffff' : '#f8fafc'),
                          borderBottom: '1px solid #e2e8f0',
                          outline: isEditing ? '2px solid #3b82f6' : 'none',
                        }}
                      >
                        <td style={{ ...tableStyles.td, color: '#94a3b8', fontWeight: '600' }}>{index + 1}</td>
                        <td style={{ ...tableStyles.td, textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 7px', borderRadius: '6px',
                            fontSize: '11px', fontWeight: '700',
                            backgroundColor: rang === '1' ? '#dbeafe' : rang === '2' ? '#dcfce7' : rang === '3' ? '#fef9c3' : '#fce7f3',
                            color: rang === '1' ? '#1e40af' : rang === '2' ? '#166534' : rang === '3' ? '#854d0e' : '#9d174d',
                          }}>{rang}</span>
                        </td>
                        <td style={{ ...tableStyles.td, textAlign: 'left', fontWeight: '500', paddingLeft: `${(parseInt(rang) - 1) * 14 + 6}px`, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {circuit.designation || '—'}
                        </td>
                        {['type', 'in', 'courbe', 'pdc', 'section', 'iz_coef', 'idn', 'tempo', 'essai', 'isol'].map(f => (
                          <td key={f} style={tableStyles.td}>{circuit[f] || ''}</td>
                        ))}
                        <td style={{ ...tableStyles.td, textAlign: 'center' }}>
                          {circuit.obs_ids?.length > 0
                            ? <span style={{ fontSize: '11px', color: '#ea580c', fontWeight: '700' }}>{circuit.obs_ids.join(', ')}</span>
                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td style={{ ...tableStyles.td, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '3px', justifyContent: 'center' }}>
                            <button onClick={() => startEditCircuit(index)} style={tableStyles.actionBtn('#3b82f6')} title="Modifier">✏️</button>
                            <button onClick={(e) => { e.stopPropagation(); openDuplicateModal(index); }} style={tableStyles.actionBtn('#7c3aed')} title="Dupliquer">📋</button>
                            <button onClick={() => handleObservationCircuit(circuit, index)} style={tableStyles.actionBtn('#f97316')} title="Observation">🔺</button>
                            {missionId && (
                              <QuickPhotoButton
                                missionId={missionId}
                                context={buildCircuitContext(circuit, index)}
                                onOpenObservation={handleQuickPhotoCircuit}
                                title="Prendre une photo du circuit (observation ensuite)"
                              />
                            )}
                            <button onClick={() => handleDeleteCircuit(index)} style={tableStyles.actionBtn('#ef4444')} title="Supprimer">🗑️</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer : 2 boutons d'action + imports IA */}
        <div style={styles.footer}>
          <button
            onClick={openCameraCapture}
            style={grilleImporting ? { ...styles.captureButton, opacity: 0.5, cursor: 'not-allowed' } : styles.captureButton}
            disabled={grilleImporting}
          >
            📷 Capturer grille
          </button>
          <button
            onClick={openFileChooser}
            style={grilleImporting ? { ...styles.uploadButton, opacity: 0.5, cursor: 'not-allowed' } : styles.uploadButton}
            disabled={grilleImporting}
          >
            📄 Choisir fichier
          </button>
          <button onClick={handleSaveAndAddNext} style={styles.saveAndNextButton}>
            💾 Enregistrer + Nouveau
          </button>
          <button onClick={handleSaveAndClose} style={styles.saveButton}>
            ✓ Fermer
          </button>
        </div>

        {showDuplicateModal && (
          <div style={styles.duplicateOverlay}>
            <div style={styles.duplicateModal}>
              <div style={styles.duplicateTitle}>Dupliquer le circuit</div>
              <div style={styles.duplicateOptions}>
                <label style={styles.duplicateOption}>
                  <input
                    type="radio"
                    name="duplicateChoice"
                    value="1"
                    checked={duplicateChoice === '1'}
                    onChange={(e) => setDuplicateChoice(e.target.value)}
                  />
                  <span>1. Au-dessous</span>
                </label>
                <label style={styles.duplicateOption}>
                  <input
                    type="radio"
                    name="duplicateChoice"
                    value="2"
                    checked={duplicateChoice === '2'}
                    onChange={(e) => setDuplicateChoice(e.target.value)}
                  />
                  <span>2. À la fin</span>
                </label>
                <label style={styles.duplicateOption}>
                  <input
                    type="radio"
                    name="duplicateChoice"
                    value="3"
                    checked={duplicateChoice === '3'}
                    onChange={(e) => setDuplicateChoice(e.target.value)}
                  />
                  <span>3. Avec enfants (hiérarchie)</span>
                </label>
              </div>
              <div style={styles.duplicateActions}>
                <button onClick={() => setShowDuplicateModal(false)} style={styles.secondaryButton}>
                  Annuler
                </button>
                <button onClick={confirmDuplicate} style={styles.primaryButton}>
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

const tableStyles = {
  th: {
    padding: '9px 6px',
    borderBottom: '2px solid #cbd5e1',
    textAlign: 'center',
    fontSize: '11px',
    fontWeight: '700',
    color: '#475569',
    whiteSpace: 'nowrap',
    backgroundColor: '#f1f5f9',
  },
  td: {
    padding: '8px 6px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#1e293b',
    whiteSpace: 'nowrap',
  },
  actionBtn: (color) => ({
    width: '28px',
    height: '28px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: `${color}22`,
    cursor: 'pointer',
    fontSize: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  }),
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '20px',
    width: '98vw',
    maxWidth: '1100px',
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '2px solid #f1f5f9'
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1e293b',
    margin: 0,
    marginBottom: '4px'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  closeButton: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#f1f5f9',
    fontSize: '20px',
    cursor: 'pointer'
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '20px'
  },
  editorCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    padding: '16px',
    marginBottom: '16px',
    boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)'
  },
  editorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px'
  },
  editorTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#0f172a'
  },
  editorActions: {
    display: 'flex',
    gap: '8px'
  },
  editorPlaceholder: {
    fontSize: '13px',
    color: '#64748b',
    backgroundColor: '#f8fafc',
    padding: '12px',
    borderRadius: '10px',
    border: '1px dashed #cbd5e1'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  formLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#475569'
  },
  formInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    outline: 'none'
  },
  formSelect: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    backgroundColor: 'white',
    outline: 'none'
  },
  secondaryButton: {
    padding: '8px 12px',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  primaryButton: {
    padding: '8px 12px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#3b82f6',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  importLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    backgroundColor: '#eff6ff',
    border: '1px solid #93c5fd',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '12px'
  },
  importLoadingSpinner: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: '3px solid #bfdbfe',
    borderTopColor: '#2563eb',
    animation: 'spin 0.8s linear infinite',
    flexShrink: 0
  },
  importLoadingTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1d4ed8',
    marginBottom: '2px'
  },
  importLoadingSubtitle: {
    fontSize: '12px',
    color: '#3b82f6'
  },
  importMessage: {
    marginBottom: '12px'
  },
  importError: {
    backgroundColor: '#fee2e2',
    color: '#b91c1c',
    padding: '10px 12px',
    borderRadius: '10px',
    fontSize: '13px',
    border: '1px solid #fecaca'
  },
  importSuccess: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    padding: '10px 12px',
    borderRadius: '10px',
    fontSize: '13px',
    border: '1px solid #86efac'
  },
  detectedBadge: {
    marginBottom: '8px',
    backgroundColor: '#eef2ff',
    color: '#3730a3',
    padding: '10px 12px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    border: '1px solid #c7d2fe'
  },
  detectedLabel: {
    fontWeight: '700'
  },
  detectedConfidence: {
    backgroundColor: '#e0e7ff',
    color: '#1e1b4b',
    padding: '2px 6px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: '700'
  },
  addFooterButton: {
    padding: '12px',
    borderRadius: '12px',
    border: '2px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    color: '#1e293b',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  captureButton: {
    padding: '12px',
    borderRadius: '12px',
    border: '2px solid #86efac',
    backgroundColor: '#ecfdf5',
    color: '#047857',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  uploadButton: {
    padding: '12px',
    borderRadius: '12px',
    border: '2px solid #c4b5fd',
    backgroundColor: '#f5f3ff',
    color: '#5b21b6',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  addButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '16px',
    cursor: 'pointer'
  },
  addButtonIcon: {
    fontSize: '20px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    border: '2px dashed #cbd5e1'
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px'
  },
  emptyText: {
    fontSize: '16px',
    color: '#64748b',
    margin: 0
  },
  circuitsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  circuitCard: {
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #e2e8f0'
  },
  circuitCardActive: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.15)'
  },
  circuitHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  circuitRang: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '700'
  },
  circuitActions: {
    display: 'flex',
    gap: '8px'
  },
  obsBtn: {
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#fee2e2',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  editBtn: {
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#dbeafe',
    fontSize: '14px',
    cursor: 'pointer'
  },
  deleteBtn: {
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#fee2e2',
    fontSize: '14px',
    cursor: 'pointer'
  },
  circuitDesignation: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '12px'
  },
  circuitDetails: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  detail: {
    fontSize: '12px',
    color: '#475569',
    backgroundColor: 'white',
    padding: '4px 10px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0'
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '20px',
    borderTop: '2px solid #f1f5f9'
  },
  duplicateOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
    padding: '20px'
  },
  duplicateModal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '18px',
    width: '100%',
    maxWidth: '360px',
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)'
  },
  duplicateTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '12px'
  },
  duplicateOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '16px'
  },
  duplicateOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: '#1e293b'
  },
  duplicateActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
  },
  saveAndNextButton: {
    flex: 1,
    padding: '14px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  },
  saveButton: {
    flex: 1,
    padding: '14px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
  }
};